import cs from 'classnames'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { Img } from '../../../ui/Img'
import { UnOrderedList } from '../../../ui/Lists'
import style from './styles/TeamCard.module.scss'

type MemberObject = {
  name: string
  medshr: string
  description: string
  photo_url: string
  logo_url: string
  subtitle1: string
  subtitle2: string
  linkedin: string
  twitter: string
  website: string
}

type PropsType = {
  memberObj: MemberObject
  teamName: string
  isAuthenticated: boolean
}

const TeamCard = ({
  memberObj: {
    name,
    medshr,
    description,
    photo_url,
    logo_url,
    subtitle1,
    subtitle2,
    linkedin,
    twitter,
    website,
  },
  teamName,
  isAuthenticated,
}: PropsType): React.ReactElement => {
  const { t } = useTranslation('common')

  const imgRource = photo_url
    ? photo_url.replace('profile-large', 'm-invite-large')
    : ''

  const styles = { image: { backgroundImage: `url('${imgRource}')` } }

  const socialLinks = initilizeSocialLinks({
    medshr,
    linkedin,
    twitter,
    website,
  })

  const iconSize = Object.keys(socialLinks).length
  const toLong = (iconSize > 2 && !medshr) || iconSize > 3
  const noSocialLinks = Object.keys(socialLinks).length === 0

  const email =
    name === 'Apply now' && /^[^@\s]+@[^@\s]+$/.test(subtitle2)
      ? subtitle2
      : 'contact@medshr.net'

  return (
    <section className={style.container}>
      <div className={style.media} style={styles.image}>
        {logo_url && (
          <Img className={style.logo} src={logo_url} alt={`${name} logo`} />
        )}
        <div className={style.overlay}>
          <div className={cs(style.scrollBlock, { [style.long]: toLong })}>
            <div>{description}</div>
          </div>

          <div className={cs(style.linkBlock)}>
            <div className={cs(style.iconGroup)}>
              <UnOrderedList nospace>
                {renderIconList(socialLinks)}
              </UnOrderedList>
            </div>
            <div
              className={cs(style.textLink, {
                [style.alone]: noSocialLinks,
                [style.space]: teamName === 'partners',
              })}
            >
              {medshr ? (
                <a
                  href={medshr}
                  className="action-link"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {teamName === 'partners' ? 'See more' : 'Connect'}
                </a>
              ) : socialLinks.website && teamName === 'partners' ? (
                <a
                  href={socialLinks.website.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="action-link"
                >
                  See more
                </a>
              ) : name === 'Apply now' ? (
                <a style={{ color: '#4b9bef' }} href={'mailto:' + email}>
                  Contact us
                </a>
              ) : isAuthenticated ? null : (
                <span
                  onClick={() => window.open('/', '_blank')}
                  className="action-link"
                >
                  {t('teampage.join_link')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {renderFooter({ name, subtitle1, subtitle2, medshr })}
    </section>
  )
}

// INITILIZE SOCIAL ICONS
export function initilizeSocialLinks({
  medshr,
  linkedin,
  twitter,
  website,
}: any): any {
  const socialLinks: any = {}

  if (medshr) socialLinks.medshr = { url: medshr, icon: 'medshr' }
  if (linkedin) socialLinks.linkedin = { url: linkedin, icon: 'linkedin' }
  if (twitter) socialLinks.twitter = { url: twitter, icon: 'twitter' }
  if (website) socialLinks.website = { url: website, icon: 'website' }

  return socialLinks
}

// MAP SOCIAL ICONS
export function renderIconList(socialLinks: any): any {
  return Object.values(socialLinks).map((item: any, i: number): any => (
    <li key={`${item.icon}${i}`} className={style.iconItem}>
      <a
        href={item.url}
        target="_blank"
        rel="noreferrer noopener"
        style={{
          backgroundImage: `url('/static/media/images/${item.icon}_icon.png')`,
        }}
      />
    </li>
  ))
}

// FOOTER
export function renderFooter(user: any): any {
  const email =
    user.name === 'Apply now' && /^[^@\s]+@[^@\s]+$/.test(user.subtitle2)
      ? user.subtitle2
      : 'contact@medshr.net'
  return (
    <footer className={style.footer}>
      <div className={style.name}>
        {user.medshr ? (
          <a href={user.medshr} target="_blank" rel="noreferrer noopener">
            {user.name}
          </a>
        ) : user.name === 'Apply now' ? (
          <a href={'mailto:' + email}>{user.name}</a>
        ) : (
          user.name
        )}
      </div>

      {user.subtitle1 && <div className={style.jobTitle}>{user.subtitle1}</div>}

      {user.subtitle2 && (
        <div className={style.jobTitle}>
          <small>{user.subtitle2}</small>
        </div>
      )}
    </footer>
  )
}

export default TeamCard
